<template>
  <article class="article-card">
    <nuxt-link :to="articleLink" class="full-width">
      <div class="image-wrapper">
        <NuxtImg
          loading="lazy"
          :src="image"
          :alt="title"
          :title="title"
        />
        <ProductLabel
          v-if="themeLabel"
          class="category-tag"
          :name="themeLabel"
          :background="themeStyle.background"
          :color="themeStyle.color"
        />
      </div>

      <div class="content">
        <h2 class="article-title">{{ title }}</h2>
        <p class="article-desc">{{ description }}</p>

        <div class="article-footer">
          <div class="niche-text" v-if="niche">
            {{ niche }}
          </div>
          <div v-else></div>
          <div class="more-link">
            {{ $t('blog.read_more') }}
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </nuxt-link>
  </article>
</template>

<script>
import ProductLabel from "~/components/molecules/ProductLabel.vue";

export default {
  components: { ProductLabel },
  props: {
    title: {
      type: String,
      required: true
    },
    slug: {
      type: String,
      required: true
    },
    image: {
      type: String,
      required: true
    },
    theme: {
      type: Object,
      default: null
    },
    category: {
      type: String,
      default: ""
    },
    description: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    niche: {
      type: String,
      default: ""
    }
  },
  computed: {
    themeObj() {
      if (this.theme && typeof this.theme === "object") return this.theme;
      return null;
    },
    themeSlug() {
      if (this.themeObj) {
        return String(this.themeObj.theme_segment ?? "");
      }

      return "";
    },
    themeLabel() {
      const theme = this.themeObj;
      const name = theme?.name ? String(theme.name).trim() : "";
      const result = name || this.category || "";
      
      return result;
    },
    themeStyle() {
      const theme = this.themeObj;
      if (!theme) return {};

      const style = {
        background: theme.background_color || undefined,
        color: theme.color || undefined,
      };

      return style;
    },
    articleLink() {
      return `/blogg/${this.themeSlug ? this.themeSlug + "/" : ""}${this.slug}`;
    }
  }
}
</script>

<style scoped lang="scss">
.article-card {
  width: 100%;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  text-align: left;
  height: 100%;
  min-height: 480px;
  // overflow: hidden;

  @include smd {
    min-height: 280px;
  }

  a {
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none;
    color: inherit;
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    aspect-ratio: 1;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .category-tag {
      position: absolute;
      bottom: 10px;
      left: -10px;
      z-index: 9999;

      @include smd {
        bottom: 10px;
        left: 0px;
      }
    }
  }

  .content {
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;

    @include smd {
      padding: 10px;
      gap: 5px;
    }

    .article-title {
      font-size: 16px;
      font-weight: 700;
      color: #000;
      line-height: 1.3;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      min-height: 52px;

      @include smd {
        font-size: 13px;
        font-weight: 600;
        min-height: 34px!important;
      }
    }

    .article-desc {
      font-size: 13px;
      color: #444;
      line-height: 1.5;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      flex: 1;
      min-height: 76px;

      @include smd {
        font-size: 11px;
        line-height: 1.4;
        height: 2.8em;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin-bottom: 0;
        flex: none;
        min-height: 32px; // Reserve 2 lines for description on mobile
      }

      @include mobile {
        -webkit-line-clamp: 2 !important;
        max-height: 2.8em !important;
        min-height: unset !important;
      }
    }

    .article-footer {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: end;
      margin-top: auto;
      width: 100%;
      gap: 5px;

      @include smd {
        padding-top: 5px;
      }

      @include smd {
        margin-top: 0;
      }

      .niche-text {
        font-size: 11px;
        font-weight: 500;
        color: #515151;
        border: 1px solid #515151;
        text-transform: uppercase;
        padding: 6px 14px 4px;
        width: fit-content;
        line-height: 1;

        @include smd {
          font-size: 7px;
          padding: 4px 10px 3px;
        }
      }

      .date {
        color: #333;
        font-size: 12px;
        font-weight: 500;
      }

      .more-link {
        font-size: 14px;
        font-weight: 500;
        color: #000;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;
        transition: gap 0.2s ease;

        @include smd {
          font-size: 11px;
        }

        svg {
          width: 9px;
          height: 10px;
          transition: transform 0.2s ease;
        }
      }
    }
  }
}
</style>
